import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { DgNumberPipe } from '../../dg-number.pipe';

@Component({
  selector: 'app-order-history-tab',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule, DgNumberPipe, TranslatePipe],
  template: `
    <div class="flex h-full flex-col">
    <div class="min-h-0 flex-1 overflow-auto">
      @if (loading) {
        <div class="flex flex-col gap-3">
          @for (row of skeletonRows; track row) {
            <div class="flex animate-pulse items-start justify-between rounded-xl border bg-card p-3">
              <div class="flex w-full flex-col gap-2">
                <div class="h-4 w-1/3 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div class="h-3 w-1/4 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div class="h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div class="h-3 w-1/5 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
              <div class="ml-3 flex shrink-0 flex-col items-end gap-3">
                <div class="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                <div class="h-3 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          }
        </div>
      } @else if (orders.length) {
        <div class="flex flex-col gap-3">
          @for (order of orders; track order) {
            <div class="flex flex-col rounded-xl border bg-card p-3">
              <div class="flex items-start justify-between">
                <div class="flex min-w-0 flex-col gap-0.5">
                  <div class="flex flex-wrap items-center gap-2">
                    <span class="font-semibold">#{{ order.serial }}</span>
                    @if (order.salesOffice) {
                    <span class="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                      <mat-icon class="icon-size-4" fontSet="material-icons-outlined">storefront</mat-icon>
                      {{ order.salesOffice }}
                    </span>
                    }
                  </div>
                  <div class="text-secondary flex flex-wrap items-center gap-x-2 text-sm">
                    <span class="text-secondary">N° ERP: {{ order.erpCode || '-' }}</span>
                    <span>{{ 'DOCUMENT_GENERATE.ORDER_HISTORY_TAB.DATE_PREFIX' | t }} {{ order.date | date:'dd-MM-yyyy' }}</span>
                  </div>
                  <span class="text-primary text-sm font-medium">{{ order.className }}</span>
                  <span class="text-secondary mt-0.5 flex items-center gap-1 text-xs">
                    <mat-icon class="icon-size-4" fontSet="material-icons-outlined">inventory_2</mat-icon>
                    {{ 'DOCUMENT_GENERATE.ORDER_HISTORY.ITEMS_COUNT' | t: { count: order.products?.length || 0 } }}
                  </span>
                </div>
                <div class="flex flex-col items-end justify-between gap-3">
                  <button mat-icon-button color="primary" type="button"
                    [matTooltip]="'DOCUMENT_GENERATE.ORDER_HISTORY.VIEW_TOOLTIP' | t" (click)="view.emit(order)">
                    <mat-icon>visibility</mat-icon>
                  </button>
                  <span class="text-secondary whitespace-nowrap text-sm">{{ order.currency }}&nbsp;{{ order.totalAmount | dgNumber }}</span>
                </div>
              </div>
              @if (order.comment) {
              <div class="mt-2 flex items-start gap-1 border-t pt-2">
                <mat-icon class="text-secondary icon-size-4 mt-0.5 shrink-0" fontSet="material-icons-outlined">sticky_note_2</mat-icon>
                <span class="text-secondary line-clamp-2 text-xs" [matTooltip]="order.comment">{{ order.comment }}</span>
              </div>
              }
            </div>
          }
        </div>
      } @else if (!loading) {
        <div class="text-secondary flex h-full items-center justify-center text-center text-sm">{{ emptyMessage || ('DOCUMENT_GENERATE.MESSAGES.NO_SUGGESTED_ORDERS' | t) }}</div>
      }
    </div>
    </div>
  `,
})
export class OrderHistoryTabComponent {
  @Input() orders: any[] = [];
  @Input() loading = false;
  /**
   * Already-translated copy. The parent owns it because the wording depends on the form state;
   * left empty, the template falls back to the generic "no suggested orders" message.
   */
  @Input() emptyMessage = '';
  @Output() view = new EventEmitter<any>();
  readonly skeletonRows = [1, 2, 3, 4];
}
